import { t } from "@impero-com/common/utils/i18n";
import { InfoTooltip } from "@impero-com/ui";
import { Checkbox } from "antd";

import { ControlPolicy } from "api/generated/model";
import { ControlPolicyTag } from "components/ControlPolicyTag";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import * as theme from "./ControlPolicyField.css";

interface ControlPolicyFieldProps {
    id: string;
    label: string;
    checkboxLabel: string;
    /** Shown in an info tooltip after the policy tag. */
    info?: string;
    policy: ControlPolicy;
    /** The value stored on the control. */
    checked: boolean;
    /** Without it, the field is read only. */
    onChange?: (checked: boolean) => void;
}

/** A control flag with the organisation policy that applies to it. */
export function ControlPolicyField({
    id,
    label,
    checkboxLabel,
    info,
    policy,
    checked,
    onChange,
}: ControlPolicyFieldProps) {
    const enforced = policy.mode === "Enforced";
    return (
        <div className={theme.field}>
            <div className={theme.labelRow}>
                <span className={theme.label}>{label}</span>
                <PolicyTag policy={policy} />
                {info !== undefined && <InfoTooltip text={info} size="small" />}
            </div>
            <Checkbox
                id={id}
                checked={enforced || checked}
                disabled={enforced || !onChange}
                onChange={(e) => onChange?.(e.target.checked)}
            >
                {checkboxLabel}
            </Checkbox>
        </div>
    );
}

function PolicyTag({ policy }: { policy: ControlPolicy }) {
    const orgSettingsEnabled = useFeatureFlag("orgSettings");
    // Organisations only choose a default on the settings page.
    if (policy.mode === "Default" && !orgSettingsEnabled) {
        return null;
    }
    return (
        <ControlPolicyTag
            policy={policy}
            defaultTooltip={t("Control_Policy_DefaultTooltip")}
            enforcedTooltip={t("Control_Policy_EnforcedTooltip")}
            placement="topLeft"
        />
    );
}
